import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import mergeQueryParams from '@/utils/mergeQueryParams';
import { type ParsedQuery, parseQuery, stringifyQuery } from '@/utils/queryParams';

type QueryParamValue = string | string[] | null | undefined;
type QueryParams = Record<string, QueryParamValue>;
type QueryParamInput = QueryParams | string | null | undefined;

interface SetQueryParamsOptions {
    replace?: boolean;
}

type SetQueryParamsFn = (...params: (QueryParamInput | SetQueryParamsOptions)[]) => void;

function isSetQueryParamsOptions(value: unknown): value is SetQueryParamsOptions {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        Object.keys(value).every((key) => key === 'replace')
    );
}

/**
 * Hook to get and update URL query parameters
 * @returns Tuple of [queryParams, setQueryParams]
 * - queryParams: Parsed query parameters from the URL
 * - setQueryParams: Function to merge and update query parameters
 *   - Pass objects to merge: setQueryParams({ key: 'value' })
 *   - Pass null/undefined values to remove keys: setQueryParams({ key: null })
 *   - Pass null to clear all params: setQueryParams(null)
 *   - Pass { replace: true } as the last argument to replace the current history entry
 */
export default function useQueryParams(): readonly [ParsedQuery<string>, SetQueryParamsFn] {
    const location = useLocation();
    const queryParams = useMemo(() => parseQuery(location.search), [location.search]);

    const navigate = useNavigate();
    const setQueryParams = useCallback<SetQueryParamsFn>(
        (...params) => {
            const lastParam = params.at(-1);
            const options = isSetQueryParamsOptions(lastParam) ? lastParam : undefined;
            const replace = options?.replace === true;
            const queryParamInputs = options ? params.slice(0, -1) : params;
            const merged = mergeQueryParams(queryParams, ...queryParamInputs);
            navigate(
                { pathname: location.pathname, search: stringifyQuery(merged) },
                { replace, state: location.state }
            );
        },
        [navigate, location.pathname, location.state, queryParams]
    );

    return [queryParams, setQueryParams] as const;
}

export { useQueryParams };
